import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import { fetchVehicleInsurances } from "../../../../api/vehicles/insurance";
import axios from "../../../../api/axios";
import { DocumentUploadSectionV5 } from "../../Media/DocumentUploadSectionV5";
import { TinyDocumentSection } from "../../documents/tinyDocumentSection";

export const VehicleDamageProcessDialog = ({ open, onClose, report, vehicleId, onUpdated }) => {
  const { t } = useTranslation();
  const [insurances, setInsurances] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    vehicle_insurance_id: "",
    occurred_at: "",
    location: "",
    description: "",
    processed: true,
    cost_nok: "",
    deductible_nok: "",
    metadata: {
      circumstances: "",
      cause: "",
      damage_extent: "",
      third_party_name: "",
      third_party_phone: "",
      third_party_registration: "",
      police_reported: false,
      police_report_number: "",
      weather: "",
      road_conditions: "",
      witnesses: "",
      immediate_actions: "",
    },
  });

  useEffect(() => {
    if (!open) return;
    async function load() {
      const ins = await fetchVehicleInsurances({ filters: [{ name: "vehicle_id", value: vehicleId }], per_page: 50 });
      setInsurances(ins?.data || []);
    }
    load();
  }, [open, vehicleId]);

  useEffect(() => {
    if (!report) return;
    setForm((prev) => ({
      ...prev,
      vehicle_insurance_id: report?.vehicle_insurance?.id || "",
      occurred_at: report?.occurred_at?.slice(0, 10) || "",
      location: report?.location || "",
      description: report?.description || "",
      processed: report?.processed ?? true,
      cost_nok: report?.cost_nok || "",
      deductible_nok: report?.deductible_nok || "",
      metadata: {
        ...prev.metadata,
        ...(report?.metadata || {}),
      },
    }));
  }, [report]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("metadata.")) {
      const key = name.replace("metadata.", "");
      setForm((prev) => ({ ...prev, metadata: { ...prev.metadata, [key]: type === "checkbox" ? checked : value } }));
    } else {
      setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        vehicle_damage_report: {
          vehicle_id: vehicleId,
          vehicle_insurance_id: form.vehicle_insurance_id || null,
          occurred_at: form.occurred_at,
          location: form.location,
          description: form.description,
          processed: form.processed,
          processed_at: new Date().toISOString(),
          cost_nok: form.cost_nok || null,
          deductible_nok: form.deductible_nok || null,
          metadata: form.metadata,
        },
      };
      const res = await axios.put(`/vehicle_damage_reports/${report.id}`, payload).then((r) => r.data);
      if (onUpdated) onUpdated(res);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PMDialog open={open} onClose={onClose} title={t("Process Damage Report")} size="7xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Insurance")}</label>
            <select name="vehicle_insurance_id" value={form.vehicle_insurance_id} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2">
              <option value="">{t("None")}</option>
              {insurances.map((ins) => (
                <option key={ins.id} value={ins.id}>
                  {ins.insurance_company} — {ins.policy_number}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Processed")}</label>
            <div className="mt-2">
              <input type="checkbox" name="processed" checked={!!form.processed} onChange={handleChange} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Occurred at")}</label>
            <input type="date" name="occurred_at" value={form.occurred_at} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Location")}</label>
            <input type="text" name="location" value={form.location} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t("Description")}</label>
            <textarea name="description" value={form.description} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" rows={3} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t("How did it happen?")}</label>
            <textarea name="metadata.circumstances" value={form.metadata.circumstances} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" rows={3} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t("Why did it happen? (Cause)")}</label>
            <textarea name="metadata.cause" value={form.metadata.cause} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" rows={3} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t("Damage extent")}</label>
            <textarea name="metadata.damage_extent" value={form.metadata.damage_extent} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" rows={3} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Third party name")}</label>
            <input type="text" name="metadata.third_party_name" value={form.metadata.third_party_name} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Third party phone")}</label>
            <input type="text" name="metadata.third_party_phone" value={form.metadata.third_party_phone} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Third party registration")}</label>
            <input type="text" name="metadata.third_party_registration" value={form.metadata.third_party_registration} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Police reported")}</label>
            <div className="mt-2">
              <input type="checkbox" name="metadata.police_reported" checked={!!form.metadata.police_reported} onChange={handleChange} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Police report number")}</label>
            <input type="text" name="metadata.police_report_number" value={form.metadata.police_report_number} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Weather")}</label>
            <input type="text" name="metadata.weather" value={form.metadata.weather} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Road conditions")}</label>
            <input type="text" name="metadata.road_conditions" value={form.metadata.road_conditions} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t("Witnesses (comma separated)")}</label>
            <input type="text" name="metadata.witnesses" value={form.metadata.witnesses} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t("Immediate actions taken")}</label>
            <textarea name="metadata.immediate_actions" value={form.metadata.immediate_actions} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" rows={3} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Estimated cost (NOK)")}</label>
            <input type="number" step="0.01" name="cost_nok" value={form.cost_nok} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("Deductible (NOK)")}</label>
            <input type="number" step="0.01" name="deductible_nok" value={form.deductible_nok} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
          </div>
        </div>

        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-700">{t("Damage documents")}</div>
          <DocumentUploadSectionV5 module="vehicle_damage_report" moduleParam={report?.id} multiple={true} iconOnly={false} />
          <TinyDocumentSection module="vehicle_damage_report" moduleParam={report?.id} title={t("Damage Documents")} showNoDocuments={true} refreshButton={true} maxDocumentDisplay={8} />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded border">{t("Cancel")}</button>
          <button type="submit" disabled={isSaving} className="px-3 py-2 rounded bg-blue-600 text-white">{isSaving ? t("Saving...") : t("Save")}</button>
        </div>
      </form>
    </PMDialog>
  );
};

export default VehicleDamageProcessDialog;


